﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

import {
    TokenRequestMode,
    TokenRequestStatus,
} from "~base/Services/API/AuthToken";
import { GetLoginInfoResponse } from "~base/Services/API/Account/Model";

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

//-------------------------------------------------------------------------------------
// State
//-------------------------------------------------------------------------------------

type UpdateFormCredentials = {
    type: "updateFormCredentials";
    username: string;
    password: string;
};
type TryFormLogin = { type: "tryFormLogin" };
type ToggleLoginPanel = { type: "toggleLoginPanel"; show: boolean };
type CompletedLoginAttempt = {
    type: "completedLoginAttempt";
    requestMode: TokenRequestMode;
    status: TokenRequestStatus;
    errorMessage: string;
    redirectUri: string;
    interactiveLogin?: boolean;
};
type RequestAspxSession = { type: "requestServerSession" };
type Navigate = { type: "navigate"; uri: string; replace?: boolean };
type SetFetchError = { type: "fetchError"; message: string };
type SetLoginInfo = { type: "setLoginInfo"; data: GetLoginInfoResponse };

export type LoginPageViewAction =
    | UpdateFormCredentials
    | ToggleLoginPanel
    | CompletedLoginAttempt
    | TryFormLogin
    | RequestAspxSession
    | Navigate
    | SetFetchError
    | SetLoginInfo;

export interface LoginPageState {
    clearToken: boolean;
    username: string;
    password: string;
    showLoginPanel: boolean;
    tryResolveFromAppStorage: boolean;
    tryResolveFromCookie: boolean;
    tryFormLogin: boolean;
    errorMessage: string;
    isLoading: boolean;
    requestServerSession: boolean;
    navigateUri: string;
    isNavigating?: boolean;
    interactiveLogin?: boolean;
    loginInfo?: GetLoginInfoResponse;
}

export function reducer<T>(
    state: LoginPageState,
    action: LoginPageViewAction
): LoginPageState {
    switch (action.type) {
        case "updateFormCredentials": {
            return {
                ...state,
                username: action.username,
                password: action.password,
            };
        }
        case "toggleLoginPanel": {
            return { ...state, showLoginPanel: action.show };
        }
        case "completedLoginAttempt": {
            return {
                ...state,
                tryResolveFromAppStorage: false,
                tryResolveFromCookie:
                    action.requestMode == "ApplicationStorage" &&
                    action.status == "Failed"
                        ? state.tryResolveFromCookie
                        : false,
                tryFormLogin: false,
                requestServerSession: false,
                isLoading: action.redirectUri != null,
                navigateUri: action.redirectUri,
                errorMessage: action.errorMessage,
                clearToken: false,
                interactiveLogin:
                    state.interactiveLogin || action.requestMode === "Forms",
            };
        }
        case "tryFormLogin": {
            return { ...state, tryFormLogin: true, errorMessage: null };
        }
        case "requestServerSession": {
            return { ...state, isLoading: true, requestServerSession: true };
        }
        case "navigate": {
            return {
                ...state,
                isLoading: true,
                navigateUri: action.uri,
                isNavigating: true,
            };
        }
        case "fetchError": {
            return {
                ...state,
                errorMessage: action.message,
            };
        }
        case "setLoginInfo": {
            return {
                ...state,
                loginInfo: action.data,
            };
        }
    }
}
